import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import userPostUserChangePassword from '../../../hooks/useApi/usePostUserChangePassword';
import ChangeUserPasswordForm from './ChangeUserPasswordForm';
import ChangeUserPasswordContext, { ChangeUserPasswordCtx } from './ChangeUserPasswordContext';
import ChangeUserPasswordDialogue from './ChangeUserPasswordDialogue';
import { ChangePasswordDialogueHandler, DialogueHandlerProps } from '../types';
import { useTranslation } from 'react-i18next';

const { Box, Title, BackButton } = vp;

type FC = FunctionComponent;

const ChangeUserPasswordContainer: FC = () => {
  const history = useHistory();
  const [ChangeUserPasswordError, setChangeUserPasswordError] = useState('');
  const [showChangeUserPasswordErrorAlert, setShowChangeUserPasswordErrorAlert] = useState(false);
  const [showChangeUserPasswordSuccessAlert, setShowChangeUserPasswordSuccessAlert] = useState(false);
  const [shouldChangeUserPassword, setShouldChangeUserPassword] = useState(false);
  const [ChangePasswordResponse, changePassword] = userPostUserChangePassword({});
  const { emailAddress } = useParams<{ emailAddress: string }>();
  const [reviewing, setReviewing] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Users.ChangeUserPassword' });

  const [defaultValues, setDefaultValues] = useState<vp.UserChangePasswordRequest>({
    EmailAddress: '',
    NewPassword: '',
    SendEmail: true,
  });

  const ChangeUserPassword: ChangeUserPasswordCtx = {
    showChangeUserPasswordSuccessAlert,
    showChangeUserPasswordErrorAlert,
    ChangeUserPasswordError,
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<ChangePasswordDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: ChangePasswordDialogueHandler = {
        change: {
          success: {
            default: () => {
              setShowChangeUserPasswordSuccessAlert(false);
              history.push('/users');
            },
          },
          fail: {
            default: () => {
              setShowChangeUserPasswordErrorAlert(false);
              setChangeUserPasswordError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onChangeUserPassword: (values: vp.UserChangePasswordRequest) => {
      setShouldChangeUserPassword(true);
      const UserChangePasswordRequest = { ...values };
      UserChangePasswordRequest.EmailAddress = emailAddress;
      changePassword(UserChangePasswordRequest);
    },

    onReview: (values: vp.UserChangePasswordRequest) => {
      setDefaultValues(values);
      setReviewing(true);
    },
  };

  useEffect(() => {
    if (shouldChangeUserPassword) {
      setShouldChangeUserPassword(false);
      const { Success, ErrorMessage } = ChangePasswordResponse;
      if (Success === true) {
        setShowChangeUserPasswordSuccessAlert(true);
      } else {
        setChangeUserPasswordError(ErrorMessage);
        setShowChangeUserPasswordErrorAlert(true);
      }
    }
  }, [ChangePasswordResponse]);

  return (
    <ChangeUserPasswordContext.Provider value={ChangeUserPassword}>
      <Box mt="sm" mb="sm" jc="fs" ai="center" gap="common">
        {reviewing ? (
          <>
            <BackButton
              variant="arrow"
              backButtonEnabled
              onClick={() => {
                setReviewing(false);
              }}
            />
            <Title title={t('Review.pageTitle')} />
          </>
        ) : (
          <>
            <BackButton variant="arrow" backButtonEnabled />
            <Title title={t('pageTitle')} />
          </>
        )}
      </Box>
      <ChangeUserPasswordForm defaultValues={defaultValues} handlers={handlers} reviewing={reviewing} />
      <ChangeUserPasswordDialogue handleDialogue={handlers.handleDialogue} />
    </ChangeUserPasswordContext.Provider>
  );
};

export default ChangeUserPasswordContainer;
